import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';
import { Label } from '@/types/workflow-types';

// Interface matching the backend AdvancedWorkflowPayload
interface AdvancedWorkflowPayload {
  id: string;
  session_id: string;
  publish_to_template?: boolean;
  name: string;
  nodes: unknown[];
  edges: unknown[];
  description?: string;
  metadata?: Record<string, unknown>;
  screenshot?: string;
  lastExecution?: {
    timestamp: string;
    nodeStatuses: Record<string, unknown[]>;
    summary?: {
      totalNodes: number;
      completedNodes: number;
      failedNodes: number;
      duration?: number;
    };
  };
}

export interface GetWorkflowResponse {
  data?: {
    success?: boolean;
    message?: string;
    workflow_id?: string;
    name?: string;
    description?: string;
    publish_to_template?: boolean;
    node_count?: number;
    edge_count?: number;
    created_at?: string;
    updated_at?: string;
    last_run_at?: string | null;
    total_runs?: number;
    latest_run_cost_usd?: number | null;
    created_by?: string;
    created_by_email?: string;
    created_by_name?: string;
    orgid?: string;
    workflow?: {
      workflow_id?: string;
      name?: string;
      description?: string;
      payload?: AdvancedWorkflowPayload;
      created_at?: string;
      updated_at?: string;
      created_by?: string;
      created_by_email?: string;
      created_by_name?: string;
      publish_to_template?: boolean;
      default_role?: string | null;
      is_locked?: boolean;
      locked_by_id?: string;
      locked_by_name?: string;
      locked_at?: string;
      labels?: Label[];
    };
    is_workflow_admin?: boolean;
  };
  detail?: string;
}

export interface GetWorkflowOptions {
  /**
   * Ask the backend to resolve the latest run's cost. It costs a Langfuse
   * round trip there, so only request it where the cost is displayed.
   */
  includeCost?: boolean;
}

/**
 * Fetches a workflow data by organization ID and workflow ID from the backend.
 * @returns Promise containing the workflow data.
 */
export async function getWorkflow(
  workflowId: string,
  orgId: string,
  options: GetWorkflowOptions = {},
): Promise<GetWorkflowResponse> {
  try {
    const query = options.includeCost ? '?include_cost=true' : '';
    const response = await apiRequest<GetWorkflowResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}${query}`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return {
        detail: response.detail || 'Failed to fetch node list',
      };
    }

    return response as GetWorkflowResponse;
  } catch (error) {
    console.error('Error fetching node list:', error);
    Sentry.captureException(error, {
      extra: { context: 'get-workflow' },
    });
    throw error;
  }
}
